// src/features/analytics/useAnalyticsPlaybackDaily.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi, type AnalyticsViewScope } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS } from '@/lib/constants/analytics';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, PlaybackDailyData } from './types';

export interface UseAnalyticsPlaybackDailyResult {
  playbackDaily: AnalyticsEnvelope<PlaybackDailyData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useAnalyticsPlaybackDaily(
  scope: AnalyticsViewScope | null,
  range: UtcRange,
  /** IANA zone for local calendar days (dashboard ADR-009); undefined = UTC days. */
  tz?: string
): UseAnalyticsPlaybackDailyResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.playbackDaily(
      scope?.kind ?? 'account',
      scope?.cid ?? 0,
      range.from,
      range.to,
      tz ?? 'utc'
    ),
    queryFn: () => AnalyticsApi.getPlaybackDaily(scope!, range, tz),
    enabled: scope !== null,
    staleTime: 60_000,
  });

  return {
    playbackDaily: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
